import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { UserPlus, Mail, KeyRound, User } from "lucide-react";
import { toast } from "sonner";
import { MolecularBg } from "../components/MolecularBg";
import { useAuth } from "../context/AuthContext";

const formatErr = (detail) => {
    if (!detail) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
};

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335"/>
    </svg>
);

export const RegisterPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { register } = useAuth();
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [submitting, setSubmitting] = useState(false);
    const searchParams = new URLSearchParams(location.search);
    const nextParam = searchParams.get("next");
    const redirect = nextParam || "/account";

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        setSubmitting(true);
        try {
            await register(form.email, form.password, form.name);
            toast.success("Account created. Welcome to Carbon Labs.");
            navigate(redirect, { replace: true });
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail) || err.message);
        }
        setSubmitting(false);
    };

    const handleGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const callback = window.location.origin + "/auth/callback";
        const redirectUrl = nextParam
            ? `${callback}?next=${encodeURIComponent(nextParam)}`
            : callback;
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="pt-28 pb-20 min-h-screen relative" data-testid="register-page">
            <MolecularBg intensity="low" />
            <div className="container-x relative z-10 max-w-md">
                <span className="eyebrow">// Account</span>
                <h1 className="font-display text-4xl md:text-5xl font-extrabold text-white tracking-tighter mt-2 mb-8 italic">
                    Create your <span className="text-[var(--gold)]">Carbon Labs</span> account
                </h1>

                <button
                    onClick={handleGoogle}
                    data-testid="google-signup-btn"
                    className="w-full flex items-center justify-center gap-3 px-5 py-3.5 rounded-full bg-white text-black font-semibold hover:bg-white/90 transition-colors mb-4"
                >
                    <GoogleIcon /> Continue with Google
                </button>

                <div className="flex items-center gap-3 my-5">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">or with email</span>
                    <div className="flex-1 h-px bg-white/10" />
                </div>

                <form onSubmit={submit} className="glass-elevated rounded-2xl p-6 space-y-4" data-testid="register-form">
                    <div>
                        <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1.5">Name (optional)</label>
                        <div className="relative">
                            <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full pl-9 pr-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                data-testid="register-name-input"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1.5">Email</label>
                        <div className="relative">
                            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                            <input
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="w-full pl-9 pr-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                data-testid="register-email-input"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1.5">Password (min 8 chars)</label>
                        <div className="relative">
                            <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                            <input
                                type="password"
                                required
                                minLength={8}
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                className="w-full pl-9 pr-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                data-testid="register-password-input"
                            />
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-solid w-full inline-flex items-center justify-center gap-2"
                        data-testid="register-submit-btn"
                    >
                        <UserPlus size={14} /> {submitting ? "Creating…" : "Create account"}
                    </button>
                </form>

                <p className="text-center mt-6 text-sm text-[var(--text-secondary)]">
                    Already have an account?{" "}
                    <Link to="/login" className="text-[var(--gold)] hover:underline" data-testid="login-link">
                        Sign in
                    </Link>
                </p>
            </div>
        </div>
    );
};
